import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../redux/store";
import { QueryStatus, axiosInstanceWithPhoneToken } from "../../../utils";
import {
  CreateCorrectiveVehicleRestitutionPayload,
  CreateInitialVehicleRestitutionPayload,
  GetInitialVehicleRestitutionBySessionResponse,
  GetVehicleRestitutionPicturesResponse,
  ValidateVehicleRestitutionPayload,
} from "./vehicleSessionsApi";

export interface VehicleSessionsState {
  createInitialVehicleRestitutionStatus: QueryStatus;
  createCorrectiveVehicleRestitutionStatus: QueryStatus;
  validateVehicleRestitutionStatus: QueryStatus;
  getVehicleRestitutionPicturesStatus: QueryStatus;
  vehicleRestitutionPictures: GetVehicleRestitutionPicturesResponse | null;
  getInitialVehicleRestitutionBySessionStatus: QueryStatus;
  initialVehicleRestitution: GetInitialVehicleRestitutionBySessionResponse;
}

const initialState: VehicleSessionsState = {
  createInitialVehicleRestitutionStatus: "idle",
  createCorrectiveVehicleRestitutionStatus: "idle",
  validateVehicleRestitutionStatus: "idle",
  getVehicleRestitutionPicturesStatus: "idle",
  vehicleRestitutionPictures: null,
  getInitialVehicleRestitutionBySessionStatus: "idle",
  initialVehicleRestitution: null,
};

export const createInitialVehicleRestitutionAsync = createAsyncThunk(
  "vehicleSessions/createInitial",
  async (payload: CreateInitialVehicleRestitutionPayload) => {
    const phoneToken = localStorage.getItem("phoneToken");
    if (!phoneToken) throw new Error();
    const axios = axiosInstanceWithPhoneToken(phoneToken);
    await axios.post("/vehicle_restitution", payload);
  },
);

export const createCorrectiveVehicleRestitutionAsync = createAsyncThunk(
  "vehicleSessions/createCorrective",
  async (payload: CreateCorrectiveVehicleRestitutionPayload) => {
    const phoneToken = localStorage.getItem("phoneToken");
    if (!phoneToken) throw new Error();
    const axios = axiosInstanceWithPhoneToken(phoneToken);
    await axios.post("/vehicle_restitution/corrective", payload);
  },
);

export const validateVehicleRestitutionAsync = createAsyncThunk(
  "vehicleSessions/validate",
  async (payload: ValidateVehicleRestitutionPayload) => {
    const phoneToken = localStorage.getItem("phoneToken");
    if (!phoneToken) throw new Error();
    const axios = axiosInstanceWithPhoneToken(phoneToken);
    await axios.post("/driver_access/vehicle_restitution/validate", payload);
  },
);

export const getVehicleRestitutionPicturesAsync = createAsyncThunk(
  "vehicleSessions/getPictures",
  async (payload: { restitution: string }) => {
    const phoneToken = localStorage.getItem("phoneToken");
    if (!phoneToken) throw new Error();
    const axios = axiosInstanceWithPhoneToken(phoneToken);
    const response = await axios.get<GetVehicleRestitutionPicturesResponse>(
      `/driver_access/vehicle_restitution/${payload.restitution}/pictures`,
    );
    return response.data;
  },
);

export const getInitialVehicleRestitutionBySessionAsync = createAsyncThunk(
  "vehicleSessions/getInitialBySession",
  async (payload: { driversHistoryUuid: string }) => {
    const phoneToken = localStorage.getItem("phoneToken");
    if (!phoneToken) throw new Error();
    const axios = axiosInstanceWithPhoneToken(phoneToken);
    const response =
      await axios.get<GetInitialVehicleRestitutionBySessionResponse>(
        `/driver_access/vehicle_restitution/initial_session/${payload.driversHistoryUuid}`,
      );
    return response.data;
  },
);

export const vehicleSessionsSlice = createSlice({
  name: "vehicle_sessions",
  initialState,
  reducers: {
    resetCreateInitialVehicleRestitutionStatus: (state) => {
      state.createInitialVehicleRestitutionStatus = "idle";
    },
    resetCreateCorrectiveVehicleRestitutionStatus: (state) => {
      state.createCorrectiveVehicleRestitutionStatus = "idle";
    },
    resetValidateVehicleRestitutionStatus: (state) => {
      state.validateVehicleRestitutionStatus = "idle";
    },
    resetGetVehicleRestitutionPicturesStatus: (state) => {
      state.getVehicleRestitutionPicturesStatus = "idle";
    },
    resetGetInitialVehicleRestitutionBySessionStatus: (state) => {
      state.getInitialVehicleRestitutionBySessionStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createInitialVehicleRestitutionAsync.pending, (state) => {
        state.createInitialVehicleRestitutionStatus = "processing";
      })
      .addCase(createInitialVehicleRestitutionAsync.fulfilled, (state) => {
        state.createInitialVehicleRestitutionStatus = "success";
      })
      .addCase(createInitialVehicleRestitutionAsync.rejected, (state) => {
        state.createInitialVehicleRestitutionStatus = "failed";
      })
      .addCase(createCorrectiveVehicleRestitutionAsync.pending, (state) => {
        state.createCorrectiveVehicleRestitutionStatus = "processing";
      })
      .addCase(createCorrectiveVehicleRestitutionAsync.fulfilled, (state) => {
        state.createCorrectiveVehicleRestitutionStatus = "success";
      })
      .addCase(createCorrectiveVehicleRestitutionAsync.rejected, (state) => {
        state.createCorrectiveVehicleRestitutionStatus = "failed";
      })
      .addCase(validateVehicleRestitutionAsync.pending, (state) => {
        state.validateVehicleRestitutionStatus = "processing";
      })
      .addCase(validateVehicleRestitutionAsync.fulfilled, (state) => {
        state.validateVehicleRestitutionStatus = "success";
      })
      .addCase(validateVehicleRestitutionAsync.rejected, (state) => {
        state.validateVehicleRestitutionStatus = "failed";
      })
      .addCase(getVehicleRestitutionPicturesAsync.pending, (state) => {
        state.getVehicleRestitutionPicturesStatus = "processing";
      })
      .addCase(
        getVehicleRestitutionPicturesAsync.fulfilled,
        (state, action) => {
          state.vehicleRestitutionPictures = action.payload;
          state.getVehicleRestitutionPicturesStatus = "success";
        },
      )
      .addCase(getVehicleRestitutionPicturesAsync.rejected, (state) => {
        state.getVehicleRestitutionPicturesStatus = "failed";
      })
      .addCase(getInitialVehicleRestitutionBySessionAsync.pending, (state) => {
        state.getInitialVehicleRestitutionBySessionStatus = "processing";
      })
      .addCase(
        getInitialVehicleRestitutionBySessionAsync.fulfilled,
        (state, action) => {
          state.initialVehicleRestitution = action.payload;
          state.getInitialVehicleRestitutionBySessionStatus = "success";
        },
      )
      .addCase(getInitialVehicleRestitutionBySessionAsync.rejected, (state) => {
        state.getInitialVehicleRestitutionBySessionStatus = "failed";
      });
  },
});

export const selectCreateInitialVehicleRestitutionStatus = (state: RootState) =>
  state.vehicleSessions.createInitialVehicleRestitutionStatus;

export const selectCreateCorrectiveVehicleRestitutionStatus = (
  state: RootState,
) => state.vehicleSessions.createCorrectiveVehicleRestitutionStatus;

export const selectValidateVehicleRestitutionStatus = (state: RootState) =>
  state.vehicleSessions.validateVehicleRestitutionStatus;

export const selectGetVehicleRestitutionPicturesStatus = (state: RootState) =>
  state.vehicleSessions.getVehicleRestitutionPicturesStatus;

export const selectVehicleRestitutionPictures = (state: RootState) =>
  state.vehicleSessions.vehicleRestitutionPictures;

export const selectGetInitialVehicleRestitutionBySessionStatus = (
  state: RootState,
) => state.vehicleSessions.getInitialVehicleRestitutionBySessionStatus;

export const selectInitialVehicleRestitution = (state: RootState) =>
  state.vehicleSessions.initialVehicleRestitution;

export default vehicleSessionsSlice.reducer;
